import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from "@mui/material";
import { CustomPropertyCategory, DeleteCustomPropertyDocument } from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  fetchCustomProperties,
  selectCalcProperties,
  selectCaseDisplayGroups,
  selectCaseProperties,
  selectLensEditorFormFieldsMap,
  selectLensEditorFormSectionsMap,
} from "redux/slices/lensEditor";
import store from "redux/store";
import { GroupLocationNames } from "./GroupLocationNames";
import { useGroupLocationNames } from "./useGroupLocationNames";

type BulkDeleteProps<T> = {
  open: boolean;
  onClose: () => void;
  lensId: number;
  propertyKeys: string[];
  propertyType: CustomPropertyCategory;
  setSelected: Dispatch<SetStateAction<T[]>>;
};
type BulkDeletePhase = "review" | "processing" | "done" | "error";

export function BulkDeletePropertiesDialog<T>({
  open,
  onClose,
  lensId,
  propertyKeys,
  propertyType,
  setSelected,
}: BulkDeleteProps<T>) {
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState(0);
  const [bulkDeletePhase, setBulkDeletePhase] = useState<BulkDeletePhase>("review");
  const fieldMap = useAppSelector(selectLensEditorFormFieldsMap);
  const sectionMap = useAppSelector(selectLensEditorFormSectionsMap);
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const calcProperties = useAppSelector(selectCalcProperties);
  const caseProperties = useAppSelector(selectCaseProperties);
  const { enqueueSnackbar } = useSnackbar();

  const PROPERTY_CATEGORY_LABELS: { [key in CustomPropertyCategory]: string } = {
    [CustomPropertyCategory.CaseCustomProperty]: "Case Properties",
    [CustomPropertyCategory.CaseCalcProperty]: "Calcs",
    [CustomPropertyCategory.DocumentProperty]: "Document Properties",
  };
  const propertyTypeName = PROPERTY_CATEGORY_LABELS[propertyType];
  const includeGroupLocations = propertyType === CustomPropertyCategory.DocumentProperty ? false : true;
  const locationLog = propertyType === CustomPropertyCategory.DocumentProperty ? "" : " that appear in Locations";

  // Load caseDisplayGroupMap from either Case Properties or Calc Properties. Not needed for Document Properties
  const caseDisplayGroupMap = useMemo(() => {
    if (propertyType === CustomPropertyCategory.CaseCustomProperty) {
      return new Map(
        caseProperties.map((prop) => [
          prop.key,
          caseDisplayGroups.filter((dg) => dg.items.some((i) => i.casePropertyKey === prop.key)),
        ])
      );
    } else if (propertyType === CustomPropertyCategory.CaseCalcProperty) {
      return new Map(
        calcProperties.map((prop) => [
          prop.key,
          caseDisplayGroups.filter((dg) => dg.items.some((i) => i.caseCalcKey === prop.key)),
        ])
      );
    } else return new Map();
  }, [propertyType, caseProperties, calcProperties, caseDisplayGroups]);

  const handleBulkDelete = async (keys: string[]) => {
    for (const key of keys) {
      try {
        await runGqlMutation(DeleteCustomPropertyDocument, {
          lensId: lensId,
          key: key,
          customPropertyCategory: propertyType,
        });
        setBulkDeleteProgress((prev) => prev + 1);
      } catch (err) {
        // Propagate error message to display in snackbar below
        console.error(err);
        throw new Error(`Exiting early -- Failed during bulk deletion on ${key}`);
      }
    }
  };

  const handleStart = () => {
    setBulkDeletePhase("processing");
    handleBulkDelete(propertyKeys)
      .then(() => setBulkDeletePhase("done"))
      .then(() => store.dispatch(fetchCustomProperties({ lensId })))
      .then(() => {
        enqueueSnackbar(`Successfully deleted ${propertyKeys.length} ${propertyTypeName}`, {
          variant: "success",
        });
      })
      .catch((err) => {
        setBulkDeletePhase("error");
        enqueueSnackbar(`${err.message}`, { variant: "error" });
      });
  };

  const handleClose = () => {
    setBulkDeletePhase("review");
    setBulkDeleteProgress(0);
    setSelected([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Delete {propertyTypeName}</DialogTitle>
      <DialogContent>
        <div className="max-h-96 overflow-y-auto p-4 text-sm">
          <Alert severity="warning">
            This will remove the values for <strong>{propertyKeys.length}</strong> {propertyTypeName} on{" "}
            <strong>every case</strong>
          </Alert>
          <p className="text-sm mb-4 font-extrabold">
            Delete {propertyKeys.length} {propertyTypeName}
            {locationLog}:
          </p>
          {includeGroupLocations && (
            <>
              {propertyKeys.map((propKey, idx) => {
                const propertyDisplayGroups = caseDisplayGroupMap.get(propKey) ?? [];
                const groupsByLocationName = useGroupLocationNames(propertyDisplayGroups, fieldMap, sectionMap);
                return (
                  <GroupLocationNames key={idx} propertyKey={propKey} groupsByLocationName={groupsByLocationName} />
                );
              })}
            </>
          )}
        </div>
        <div className="mt-6 h-12">
          {(bulkDeletePhase === "processing" || bulkDeletePhase === "error") && (
            <>
              <p className="text-sm mb-1">
                Progress: {bulkDeleteProgress} / {propertyKeys.length}
              </p>
              <LinearProgress variant="determinate" value={(bulkDeleteProgress / propertyKeys.length) * 100} />
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {bulkDeletePhase === "review" && (
          <>
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button onClick={handleStart} variant="contained" color="primary">
              OK
            </Button>
          </>
        )}
        {(bulkDeletePhase === "done" || bulkDeletePhase === "error") && (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
